import { BaseView, PromiseCompletionSource } from "scene-manager";
import { ICanvasScaler } from "../../../canvas/ICanvasScaler";
import { Scales } from "../../../canvas/CanvasScaler";
import { Container, IDestroyOptions, Ticker } from "pixi.js";

export abstract class AdaptiveView<TResult> extends BaseView<TResult> {
    protected frame: Container;
    protected targetShowY: number;
    protected targetHideY: number;
    protected updateFn?: (delta: number) => void;

    public constructor(
        protected readonly canvasScaler: ICanvasScaler,
        operationControls: PromiseCompletionSource<TResult>
    ) {
        super(operationControls);
        this.canvasScaler.onResize.add(this.onResize, this);
    }

    public override destroy(options?: IDestroyOptions | boolean): void {
        this.canvasScaler.onResize.remove(this.onResize, this);
        super.destroy(options);
    }
    
    protected abstract layout(width: number, height: number, initialLayout: boolean): void;

    private onResize(scales: Scales): void {
        this.layout(scales.width, scales.height, false);
    }

    /**
     * Ease out elastic function with slower growth and limited overshoot (~1.2)
     * @param t Progress from 0 to 1
     * @returns Eased value
     */
    private easeOutElasticLimited(t: number): number {
        const overshoot = 0.2; // Max overshoot above 1

        if (t === 0) return 0;
        if (t === 1) return 1;

        const base = 1 - Math.pow(1 - t, 3);
        const overshootEffect = Math.sin(t * Math.PI) * overshoot;

        return base + overshootEffect;
    }

    public show(duration: number = 1000): Promise<void> {
        if (this.frame == null) {
            console.warn("You try to animate view.frame sowing, but frame is null!");
            return;
        }
        return new Promise((resolve) => {
            if (this.updateFn) {
                Ticker.shared.remove(this.updateFn);
            }

            const startY = this.frame.position.y;
            const changeY = this.targetShowY - startY;
            let elapsed = 0;

            this.updateFn = (_delta: number) => {
                elapsed += Ticker.shared.elapsedMS;
                const t = Math.min(elapsed / duration, 1);
                const eased = this.easeOutElasticLimited(t);

                this.frame.y = startY + changeY * eased;

                if (t >= 1) {
                    Ticker.shared.remove(this.updateFn!);
                    this.updateFn = undefined;
                    resolve();
                }
            };

            Ticker.shared.add(this.updateFn);
        });
    }

    protected easeInBack(t: number): number {
        const s = 1.70158;
        return t === 0
            ? 0
            : t === 1
                ? 1
                : t * t * ((s + 1) * t - s);
    }

    public hide(duration = 800): Promise<void> {
        if (this.frame == null) {
            console.warn("You try to animate view.frame hiding, but frame is null!");
            return;
        }
        return new Promise((resolve) => {
            if (this.updateFn) {
                Ticker.shared.remove(this.updateFn);
            }

            const startY = this.frame.y;
            const changeY = this.targetHideY - startY;
            let elapsed = 0;

            this.updateFn = (_delta: number) => {
                elapsed += Ticker.shared.elapsedMS;
                const t = Math.min(elapsed / duration, 1);
                const eased = this.easeInBack(t);

                this.frame.y = startY + changeY * eased;

                if (t >= 1) {
                    Ticker.shared.remove(this.updateFn!);
                    this.updateFn = undefined;
                    resolve();
                }
            };

            Ticker.shared.add(this.updateFn);
        });
    }
}
